import { alpha, Box, IconButton, TextField, keyframes } from '@mui/material';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import readyIcon from '../../assets/images/naming-wizard-ready.png';
import suggestionIcon from '../../assets/images/naming-wizard-suggestion.png';

const suggestionPopIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  55% {
    opacity: 1;
    transform: scale(1.08);
  }
  75% {
    transform: scale(0.96);
  }
  90% {
    transform: scale(1.03);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

interface NamingSuggestionFieldProps {
  suggestedName: string;
  value: string;
  onChange: (value: string) => void;
}

export default function NamingSuggestionField({ suggestedName, value, onChange }: NamingSuggestionFieldProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const [isEditing, setIsEditing] = useState(false);
  const cancelEdit = (): void => {
    onChange(suggestedName);
    setIsEditing(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={readyIcon} alt="" sx={{ width: 24, height: 24 }} />
          <Typography variant="titleMedium">{t('products:naming_wizard.ready_title')}</Typography>
        </Box>
        <Typography variant="bodySmall">{t('products:naming_wizard.ready_description')}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          backgroundColor: 'primaryContainer.main',
          maxWidth: 640,
          alignSelf: 'center',
          width: '100%',
          transformOrigin: 'center center',
          animation: `${suggestionPopIn} 0.55s cubic-bezier(0.34, 1.4, 0.64, 1) both`,
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="img" src={suggestionIcon} alt="" sx={{ width: 32, height: 32 }} />
          <Typography variant="labelSmall" color="onPrimaryContainer">
            {t('products:naming_wizard.our_suggestion')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 2,
            pr: 0.5,
            py: isEditing ? 0.5 : 0,
            borderRadius: 2,
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.15)',
            backgroundColor: isEditing ? undefined : (theme) => alpha(theme.palette.onPrimary.main, 0.16),
          }}
        >
          {isEditing ? (
            <TextField
              fullWidth
              autoFocus
              variant="outlined"
              autoComplete="off"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '3px',
                },
              }}
            />
          ) : (
            <Typography variant="bodyMedium" color="onPrimaryContainer" sx={{ flex: 1, minWidth: 0 }}>
              {value}
            </Typography>
          )}
          <IconButton
            aria-label={isEditing ? t('common:cancel') : t('common:modify')}
            onClick={isEditing ? cancelEdit : () => setIsEditing(true)}
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          >
            {isEditing ? <CloseIcon /> : <EditOutlinedIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
